import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  Backdrop,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveIcon from '@mui/icons-material/Save';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import Swal from 'sweetalert2';

const selectBoxHeight = 45; // Set the height for the select box

const userRegistrationSchema = Joi.object({
  user_name: Joi.string().min(3).max(50).required().messages({
    'string.min': 'Username must be at least 3 characters',
    'string.max': 'Username cannot exceed 50 characters',
    'any.required': 'Username is required',
  }),
  password: Joi.string().min(6).required().messages({
    'string.min': 'Password must be at least 6 characters',
    'any.required': 'Password is required',
  }),
  confirm_password: Joi.string()
    .valid(Joi.ref('password'))
    .required()
    .messages({
      'any.only': 'Passwords do not match',
      'any.required': 'Confirm password is required',
    }),
  irm_user_types_id: Joi.number().required().messages({
    'any.required': 'User type is required',
  }),
  first_name: Joi.string().max(100).required().messages({
    'string.max': 'First name cannot exceed 100 characters',
    'any.required': 'First name is required',
  }),
  middle_name: Joi.string().max(100).allow('', null).optional(),
  last_name: Joi.string().max(100).allow('', null).optional(),
  external_code: Joi.string().max(50).allow('', null).optional(),
  email_address: Joi.string().email({ tlds: false }).max(128).allow('', null).optional(),
  phone_number: Joi.string()
    .pattern(/^[0-9]{10}$/)
    .required()
    .messages({
      'string.pattern.base': 'Phone number must be exactly 10 digits',
      'any.required': 'Phone number is required',
    }),
  country_code: Joi.string().max(8).allow('', null).optional().default('91'),
  occupation: Joi.string().max(128).allow('', null).optional(),
  irm_countries_id: Joi.number().allow(null).optional(),
});

const defaultValues = {
  user_name: '',
  password: '',
  confirm_password: '',
  irm_user_types_id: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  external_code: '',
  email_address: '',
  phone_number: '',
  country_code: '91',
  occupation: '',
  irm_countries_id: 100,
};

function UserRegistration() {
  const [loading, setLoading] = useState(false);
  const [userTypes, setUserTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: joiResolver(userRegistrationSchema),
  });

  // Fetch user types
  useEffect(() => {
    const fetchUserTypes = async () => {
      try {
        const response = await api.user.getActiveUserTypes();
        if (response.data && response.data.data) {
          setUserTypes(response.data.data);
        } else if (Array.isArray(response.data)) {
          setUserTypes(response.data);
        }
      } catch (error) {
        console.error('Error fetching user types:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load user types',
        });
      }
    };

    fetchUserTypes();
  }, []);

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.master.getActiveCountries();
        if (response.data && response.data.countries) {
          setCountries(response.data.countries);
        } else if (Array.isArray(response.data)) {
          setCountries(response.data);
        } else {
          setCountries([]);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        setCountries([]);
      }
    };

    fetchCountries();
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Remove confirm_password from submission
      const { confirm_password, ...submitData } = data;

      // Convert empty strings to null or remove them
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === null || submitData[key] === undefined) {
          if (key !== 'middle_name' && key !== 'last_name' && key !== 'external_code' && 
              key !== 'email_address' && key !== 'occupation' && key !== 'country_code') {
            // Keep required fields even if empty (will be caught by validation)
          } else {
            // Remove optional empty fields
            delete submitData[key];
          }
        }
      });

      const response = await api.contacts.userRegistration(submitData);

      // Close loader before showing success message
      setLoading(false);

      // Check if response is successful
      if (response.data) {
        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'User created successfully',
          confirmButtonText: 'OK',
        });
        reset(defaultValues);
      } else {
        throw new Error('Failed to create user');
      }
    } catch (error) {
      // Close loader before showing error message
      setLoading(false);
      console.error('Error creating user:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to create user',
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h4" fontWeight="medium" mb={3}>
              User Registration
            </MDTypography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {/* User Credentials Section */}
                <Grid item xs={12}>
                  <MDTypography variant="h6" fontWeight="medium" color="info" mb={2}>
                    Login Credentials
                  </MDTypography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="user_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Username *"
                        fullWidth
                        margin="normal"
                        error={!!errors.user_name}
                        helperText={errors.user_name?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Password *"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="confirm_password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm Password *"
                        type={showConfirmPassword ? 'text' : 'password'}
                        fullWidth
                        margin="normal"
                        error={!!errors.confirm_password}
                        helperText={errors.confirm_password?.message}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="irm_user_types_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal" error={!!errors.irm_user_types_id}>
                        <InputLabel id="user-type-label">User Type *</InputLabel>
                        <Select
                          {...field}
                          labelId="user-type-label"
                          label="User Type *"
                          sx={{
                            height: selectBoxHeight,
                          }}
                        >
                          <MenuItem value="" disabled>
                            <em>Select User Type</em>
                          </MenuItem>
                          {userTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography color="error" variant="caption">
                          {errors.irm_user_types_id?.message}
                        </Typography>
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Personal Information Section */}
                <Grid item xs={12} mt={3}>
                  <MDTypography variant="h6" fontWeight="medium" color="info" mb={2}>
                    Personal Information
                  </MDTypography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="first_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="First Name *"
                        fullWidth
                        margin="normal"
                        error={!!errors.first_name}
                        helperText={errors.first_name?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="middle_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Middle Name"
                        fullWidth
                        margin="normal"
                        error={!!errors.middle_name}
                        helperText={errors.middle_name?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="last_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Last Name"
                        fullWidth
                        margin="normal"
                        error={!!errors.last_name}
                        helperText={errors.last_name?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="external_code"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="External Code / Employee ID"
                        fullWidth
                        margin="normal"
                        error={!!errors.external_code}
                        helperText={errors.external_code?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="email_address"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Email Address"
                        type="email"
                        fullWidth
                        margin="normal"
                        error={!!errors.email_address}
                        helperText={errors.email_address?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Controller
                    name="occupation"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Occupation"
                        fullWidth
                        margin="normal"
                        error={!!errors.occupation}
                        helperText={errors.occupation?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                {/* Contact Information Section */}
                <Grid item xs={12} mt={3}>
                  <MDTypography variant="h6" fontWeight="medium" color="info" mb={2}>
                    Contact Information
                  </MDTypography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="country_code"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Country Code"
                        fullWidth
                        margin="normal"
                        error={!!errors.country_code}
                        helperText={errors.country_code?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={5}>
                  <Controller
                    name="phone_number"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Phone Number *"
                        fullWidth
                        margin="normal"
                        error={!!errors.phone_number}
                        helperText={errors.phone_number?.message}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Controller
                    name="irm_countries_id"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth margin="normal" error={!!errors.irm_countries_id}>
                        <InputLabel id="country-label">Country</InputLabel>
                        <Select
                          {...field}
                          labelId="country-label"
                          label="Country"
                          sx={{
                            height: selectBoxHeight,
                          }}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>
                              {country.country_name}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography color="error" variant="caption">
                          {errors.irm_countries_id?.message}
                        </Typography>
                      </FormControl>
                    )}
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <MDBox 
                    display="flex" 
                    justifyContent="flex-end" 
                    gap={2} 
                    mt={4}
                    sx={{
                      borderTop: '1px solid',
                      borderColor: 'grey.300',
                      pt: 3,
                    }}
                  >
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => reset(defaultValues)}
                      size="large"
                      sx={{
                        minWidth: '120px',
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Reset
                    </MDButton>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      startIcon={<SaveIcon />}
                      disabled={loading}
                      size="large"
                      sx={{
                        minWidth: '150px',
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {loading ? 'Creating...' : 'Create User'}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </MDBox>

      <Footer />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </DashboardLayout>
  );
}

export default UserRegistration;
